import React, { useEffect, useRef, useState } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { LexRuntimeV2Client, RecognizeTextCommand } from '@aws-sdk/client-lex-runtime-v2';
import { fromCognitoIdentityPool } from '@aws-sdk/credential-provider-cognito-identity';
import { CognitoIdentityClient } from '@aws-sdk/client-cognito-identity';
import ConfigService from '../ConfigService';
import appConfig from '../app.config';

const AwsChatbot = () => {
  const { authState, oktaAuth } = useOktaAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const scrollRef = useRef(null);
  const lexClientRef = useRef(null);
  const lexConfigRef = useRef(null);
  const sessionIdRef = useRef(`reactjs-${Date.now()}`);

  useEffect(() => {
    if (!authState?.isAuthenticated || lexClientRef.current) return;

    const init = async () => {
      try {
        const [user, runtimeConfig] = await Promise.all([
          oktaAuth.getUser(),
          ConfigService.initialize()
        ]);

        const region = runtimeConfig?.AWS_REGION || 'eu-central-1';
        const cognitoPoolId = runtimeConfig?.AWS_COGNITO_ID_POOL;

        lexConfigRef.current = {
          botId: runtimeConfig?.AWS_LEX_BOT_ID || appConfig.lexBotId,
          botAliasId: runtimeConfig?.AWS_LEX_BOT_ALIAS_ID || appConfig.lexBotAliasId,
          localeId: runtimeConfig?.AWS_LEX_LOCALE || appConfig.lexLocaleId || 'en_US',
          sessionAttributes: {
            userEmail: user.email,
            sourcePlatform: 'ReactJS-Dashboard',
            userTier: user.tier || 'STANDARD'
          }
        };

        lexClientRef.current = new LexRuntimeV2Client({
          region,
          credentials: cognitoPoolId
            ? fromCognitoIdentityPool({
                client: new CognitoIdentityClient({ region }),
                identityPoolId: cognitoPoolId
              })
            : undefined
        });
      } catch (error) {
        console.error('Error initializing Lex client:', error);
      }
    };

    init();
  }, [authState, oktaAuth]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isLoading]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || !lexClientRef.current) return;

    setMessages(prev => [...prev, { from: 'user', text }]);
    setInput('');
    setIsLoading(true);

    try {
      const { botId, botAliasId, localeId, sessionAttributes } = lexConfigRef.current;
      const cmd = new RecognizeTextCommand({
        botId,
        botAliasId,
        localeId,
        sessionId: sessionIdRef.current,
        text,
        sessionState: { sessionAttributes }
      });
      const response = await lexClientRef.current.send(cmd);
      const replies = response.messages?.map(m => ({ from: 'bot', text: m.content })).filter(m => m.text) || [];
      setMessages(prev => [...prev, ...replies]);
    } catch (error) {
      console.error('Lex error:', error);
      setMessages(prev => [...prev, { from: 'bot', text: 'Sorry, something went wrong.' }]);
    } finally {
      setIsLoading(false);
    }
  };

  if (!authState?.isAuthenticated) return null;

  return (
    <div className="chatbot-widget">
      <div className="chatbot-trigger" onClick={() => setOpen(o => !o)} title="Open Support Chat">
        <span className="bot-icon">🤖</span>
      </div>
      {open && (
        <div className="chatbot-window">
          <div className="chatbot-messages" ref={scrollRef}>
            {messages.map((m, i) => (
              <div key={i} className={`chatbot-msg chatbot-msg--${m.from}`}>{m.text}</div>
            ))}
            {isLoading && <div className="chatbot-msg chatbot-msg--bot">...</div>}
          </div>
          <div className="chatbot-input">
            <input
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && !isLoading && sendMessage()}
              placeholder="Type a message…"
              disabled={isLoading}
            />
            <button onClick={sendMessage} disabled={isLoading || !input.trim()}>Send</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AwsChatbot;